import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  FolderArchive, Download, Search, Filter, RefreshCw,
  FileText, CheckSquare, Square, Package
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ORDER = [
  'Templates',
  'Excel Reports',
  'SOPs',
  'Vehicle Documents',
  'Driver Documents',
  'Incidents',
  'Complaints & Compliments',
  'Recruitment',
  'Training Records',
  'Operational',
];

const SchedulesTemplatesPage = () => {
  useAuth();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState({});
  const [activeCategory, setActiveCategory] = useState('Templates');
  const [subcategory, setSubcategory] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [downloading, setDownloading] = useState(false);

  const fetchCatalog = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/document-catalog`);
      setItems(res.data.items || []);
      setCategories(res.data.categories || {});
    } catch (e) {
      toast.error('Failed to load document catalog');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCatalog(); }, []);

  const orderedCategories = useMemo(() => {
    const keys = Object.keys(categories);
    return [
      ...CATEGORY_ORDER.filter(c => keys.includes(c)),
      ...keys.filter(c => !CATEGORY_ORDER.includes(c)),
    ];
  }, [categories]);

  useEffect(() => {
    if (orderedCategories.length > 0 && !orderedCategories.includes(activeCategory)) {
      setActiveCategory(orderedCategories[0]);
    }
  }, [orderedCategories, activeCategory]);

  const subcats = useMemo(
    () => Object.keys(categories[activeCategory] || {}).sort(),
    [categories, activeCategory]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(it => {
      if (it.category !== activeCategory) return false;
      if (subcategory !== 'all' && it.subcategory !== subcategory) return false;
      if (!q) return true;
      const hay = `${it.name || ''} ${it.filename || ''} ${it.vehicle_registration || ''} ${it.driver_name || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, activeCategory, subcategory, search]);

  const toggle = (path) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  };

  const toggleAllVisible = () => {
    const visiblePaths = filtered.map(f => f.disk_path).filter(Boolean);
    const allSelected = visiblePaths.length > 0 && visiblePaths.every(p => selected.has(p));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) {
        visiblePaths.forEach(p => next.delete(p));
      } else {
        visiblePaths.forEach(p => next.add(p));
      }
      return next;
    });
  };

  const downloadZip = async (paths) => {
    if (!paths || paths.length === 0) {
      toast.error('Select at least one document');
      return;
    }
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/document-catalog/download-zip`,
        paths,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `documents_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${paths.length} document(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const downloadSingle = async (item) => {
    const url = item.file_url || '';
    const filename = item.filename || 'download';
    // Live-generated Excel exports, static templates, and DB-blob docs — fetch as blob with auth headers (axios defaults)
    if (
      item.is_live_export ||
      item.is_db_blob ||
      url.includes('/export/excel') ||
      url.includes('/audit-logs/export') ||
      url.includes('/export/complaints') ||
      url.includes('/export/compliments') ||
      url.includes('/export/assessments') ||
      url.includes('/export/checklists') ||
      url.includes('/export/baseline') ||
      url.includes('/export/routes') ||
      url.includes('/export/speeding') ||
      url.includes('/export/fatigue') ||
      url.includes('/templates/') ||
      url.includes('/template/') ||
      url.includes('download-template') ||
      url.includes('download-form-template') ||
      url.includes('download-all-templates') ||
      url.includes('download-test-template')
    ) {
      try {
        const fullUrl = url.startsWith('/') ? `${API_URL}${url}` : url;
        const res = await axios.get(fullUrl, { responseType: 'blob' });
        const blob = new Blob([res.data], {
          type: res.headers['content-type'] || 'application/octet-stream',
        });
        const href = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(href);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Download failed');
      }
      return;
    }
    // Static uploaded files — plain anchor is fine (served from /api/uploads which auth-allows)
    const fullUrl = url.startsWith('/') ? `${API_URL}${url}` : url;
    const a = document.createElement('a');
    a.href = fullUrl;
    a.download = filename;
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const selectedPaths = Array.from(selected);
  const allFilteredPaths = filtered.map(f => f.disk_path).filter(Boolean);
  const allVisibleSelected = allFilteredPaths.length > 0 && allFilteredPaths.every(p => selected.has(p));

  return (
    <div className="space-y-6" data-testid="schedules-templates-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-3" style={{ fontFamily: 'Barlow Condensed' }}>
            <FolderArchive className="w-7 h-7" /> SCHEDULES &amp; TEMPLATES
          </h1>
          <p className="text-slate-500 mt-1">Central download hub for every template, document, and uploaded file across the platform.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchCatalog} disabled={loading} data-testid="refresh-catalog-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Category Tabs */}
      <Tabs value={activeCategory} onValueChange={(v) => { setActiveCategory(v); setSubcategory('all'); }}>
        <TabsList className="flex flex-wrap h-auto">
          {orderedCategories.map(c => (
            <TabsTrigger key={c} value={c} data-testid={`cat-tab-${c.toLowerCase().replace(/\s+/g, '-')}`}>
              {c}
              <Badge variant="secondary" className="ml-2">
                {Object.values(categories[c] || {}).reduce((a, b) => a + b, 0)}
              </Badge>
            </TabsTrigger>
          ))}
        </TabsList>

        {orderedCategories.map(c => (
          <TabsContent key={c} value={c} className="mt-4 space-y-4">
            {/* Filters Bar */}
            <Card>
              <CardContent className="pt-4 space-y-3">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                    <Search className="w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by name, vehicle, driver, filename..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="max-w-md"
                      data-testid="catalog-search"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-slate-400" />
                    <Select value={subcategory} onValueChange={setSubcategory}>
                      <SelectTrigger className="w-[220px]" data-testid="catalog-subcategory">
                        <SelectValue placeholder="All sub-categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All sub-categories</SelectItem>
                        {subcats.map(s => (
                          <SelectItem key={s} value={s}>
                            {s} ({categories[c]?.[s] || 0})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-2 border-t">
                  <div className="text-sm text-slate-500">
                    Showing <strong>{filtered.length}</strong> of {Object.values(categories[c] || {}).reduce((a, b) => a + b, 0)}
                    {selected.size > 0 && <span className="ml-3 text-slate-700">({selected.size} selected)</span>}
                  </div>
                  <div className="flex-1" />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadZip(selectedPaths)}
                    disabled={downloading || selected.size === 0}
                    data-testid="download-selected-btn"
                  >
                    <Download className="w-4 h-4 mr-1" /> Download Selected ({selected.size})
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadZip(allFilteredPaths)}
                    disabled={downloading || filtered.length === 0}
                    data-testid="download-filtered-btn"
                  >
                    <Package className="w-4 h-4 mr-1" /> Download All Filtered ({filtered.length})
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Document Table */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-600" />
                  {c} — {subcategory === 'all' ? 'All Documents' : subcategory}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="py-16 text-center text-slate-400">
                    <RefreshCw className="w-6 h-6 animate-spin inline mr-2" /> Loading catalog...
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="py-16 text-center text-slate-400">
                    No documents found for this filter.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <button onClick={toggleAllVisible} data-testid="select-all-visible-btn">
                            {allVisibleSelected
                              ? <CheckSquare className="w-4 h-4 text-slate-700" />
                              : <Square className="w-4 h-4 text-slate-400" />}
                          </button>
                        </TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Sub-category</TableHead>
                        <TableHead>Filename</TableHead>
                        <TableHead>Uploaded</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((it, idx) => (
                        <TableRow key={`${it.disk_path}-${idx}`} data-testid={`catalog-row-${idx}`}>
                          <TableCell>
                            <button onClick={() => toggle(it.disk_path)}>
                              {selected.has(it.disk_path)
                                ? <CheckSquare className="w-4 h-4 text-emerald-600" />
                                : <Square className="w-4 h-4 text-slate-400" />}
                            </button>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm font-medium text-slate-900">{it.name}</div>
                            {(it.vehicle_registration || it.driver_name) && (
                              <div className="text-xs text-slate-500">
                                {it.vehicle_registration || it.driver_name}
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{it.subcategory}</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-slate-500 font-mono max-w-[280px] truncate">
                            {it.filename}
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">
                            {it.uploaded_at ? String(it.uploaded_at).slice(0, 10) : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => downloadSingle(it)}
                              title="Download"
                              data-testid={`download-single-${idx}`}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default SchedulesTemplatesPage;
